import {
  type ActivePlanKey,
  comparePlans,
} from '@sparklayer-io/spark-dash-core/constants/features';
import { cn } from '@sparklayer-io/spark-dash-ui/lib/styles';
import { Text } from '@ui/themed';
import { t } from 'i18next';

/** Separators for the plan list, so the conjunction stays correct in every locale. */
const planListFormatter = new Intl.ListFormat(undefined, { style: 'long', type: 'conjunction' });

/**
 * Chip gradient per plan tier, based on BillingPlanGradients.
 */
const PLAN_CHIP_GRADIENTS: Partial<Record<ActivePlanKey, string>> = {
  v2_basic: 'from-[#ffb87a] to-[#ff9443]',
  v2_starter_plus: 'from-[#ffb87a] to-[#ff9443]',
  v2_growth: 'from-[#66ffb8] to-[#088012]',
  v2_pro: 'from-gray-700 to-black',
  enterprise: 'from-[#b318ff] to-[#1e43ff]',
  enterprise_onboarding: 'from-[#b318ff] to-[#1e43ff]',
};
const DEFAULT_CHIP_GRADIENT = 'from-gray-500 to-gray-900';

/** A single plan name as a chip, tinted by plan tier and labelled from the current locale. */
export function PlanBadge({ plan }: { plan: ActivePlanKey }) {
  const gradient = PLAN_CHIP_GRADIENTS[plan] ?? DEFAULT_CHIP_GRADIENT;
  return (
    <span
      className={cn(
        'inline-block rounded bg-linear-to-r px-1 py-px align-middle text-[10px] text-white',
        gradient,
      )}
    >
      {t(`labels:plans.${plan}`)}
    </span>
  );
}

/**
 * "Available on: Starter, Growth, and Pro" — every plan gets its own chip, cheapest first
 * whatever order the caller passed. Plans that share a display name (`v2_basic` and
 * `v2_starter_plus` are both "Starter") collapse into a single chip.
 */
export function PlanAvailabilityBadge({ plans }: { plans: ActivePlanKey[] }) {
  const planByName = new Map<string, ActivePlanKey>();
  for (const plan of [...plans].sort(comparePlans)) {
    const name = t(`labels:plans.${plan}`);
    if (!planByName.has(name)) planByName.set(name, plan);
  }
  const names = [...planByName.keys()];

  return (
    <span className="inline-flex items-center rounded-full border border-spark-border-default bg-spark-surface-subtle px-3 py-1">
      <Text variant="secondary">
        {t('labels:available_on')}{' '}
        {planListFormatter.formatToParts(names).map((part, index) =>
          part.type === 'element' ? (
            // biome-ignore lint/style/noNonNullAssertion: part.value is always a name we just inserted into planByName
            <PlanBadge key={part.value} plan={planByName.get(part.value)!} />
          ) : (
            // biome-ignore lint/suspicious/noArrayIndexKey: formatted separators are static text in a fixed order
            <span key={`separator-${index}`}>{part.value}</span>
          ),
        )}
      </Text>
    </span>
  );
}
